import { getTheme, Stack } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPFontIcon, IPLink, IPScrollablePane, IPSpinner, IPText } from 'baseComponents';
import { IPList } from 'baseComponents/List/IPList';
import { IPScrollbarVisibility } from 'baseComponents/ScrollablePane/IPScrollablePane';
import { IPSpinnerSize } from 'baseComponents/Spinner/IPSpinner';
import { IPStack } from 'baseComponents/Stack/IPStack';
import LinkConfirmationModal from 'components/Links/Preview/LinkConfirmationModal';
import { LinkPreviewModalOrUrl } from 'components/Links/Preview/LinkPreviewModalOrUrl';
import DefaultEmptyStateImage, { DefaultEmptyStateSize } from 'components/Utils/IconsAndImages/DefaultEmptyStateImage';
import DateDividerListItem, { DateDivider } from 'components/Utils/ListItemDateDividers';
import { circleIcon, globalStackTokensGapSmall, validIcon } from 'globalStyles';
import ResourceLink from 'models/resourceLink';
import { FunctionComponent, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiGetWidgetDataUserLinks } from 'services/Api/dashboardService';
import { apiRequest } from 'services/Auth/authConfig';
import { toLocaleDateShort } from 'utils/datetime';
import { sortOnString } from 'utils/sorting';
import { IWidgetRendererProps } from '../WidgetRenderer';

interface IWidgetLibraryMyItemsProps extends IWidgetRendererProps {}

const WidgetLibraryMyItems: FunctionComponent<IWidgetLibraryMyItemsProps> = (props: IWidgetLibraryMyItemsProps) => {
  const { t } = useTranslation(['translation', 'widgets']);
  const appContext = useContext(AppContext);
  const [links, setLinks] = useState<(ResourceLink | DateDivider)[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [linkToShow, setLinkToShow] = useState<ResourceLink | undefined>(undefined);
  const [showConfirmationModal, setShowConfirmationModal] = useState<ResourceLink | undefined>(undefined);
  const theme = getTheme();

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.widget]);

  const loadData = async () => {
    try {
      if (isLoading) return;
      setIsLoading(true);

      await appContext.globalDataCache.lists.getItems();
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);

      const links = await apiGetWidgetDataUserLinks(accessToken, appContext.globalDataCache, props.abortSignal);

      //sort links - those with read confirmations first, then by name
      links.sort((a, b) => {
        const hasA = a.hasRCUnread();
        const hasB = b.hasRCUnread();
        if (hasA && !hasB) return -1;
        if (!hasA && hasB) return 1;
        if (hasA && hasB) return sortOnString(a.linkName, b.linkName);

        const hasRA = a.hasRCRead();
        const hasRB = b.hasRCRead();
        if (hasRA && !hasRB) return -1;
        if (!hasRA && hasRB) return 1;

        return sortOnString(a.linkName, b.linkName);
      });

      //insert dividers
      const items: (ResourceLink | DateDivider)[] = [...links];
      let hasRead: boolean = false;
      let hasConfirmation: boolean = false;

      for (let idx = 0; idx < items.length; idx++) {
        const item = items[idx] as ResourceLink;
        if (!hasConfirmation && item.hasRCUnread()) {
          items.splice(idx++, 0, new DateDivider(t('widgets:WidgetLibraryMyItems.NeedReadConfirmation')));
          hasConfirmation = true;
        } else if (!hasRead && item.hasRCRead()) {
          items.splice(idx++, 0, new DateDivider(t('widgets:WidgetLibraryMyItems.Confirmed')));
          hasRead = true;
        } else if (!item.hasRC()) {
          if (hasConfirmation || hasRead) {
            //only add "other" if there are other sections
            items.splice(idx++, 0, new DateDivider(t('widgets:WidgetLibraryMyItems.Other')));
          }
          break; //all others will be "other"
        }
      }

      setLinks(items);
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const getLinksToConfirm = (): ResourceLink[] => {
    return links.filter((l) => l instanceof ResourceLink && l.hasRCUnread()) as ResourceLink[];
  };

  const onRenderItem = (item: ResourceLink | DateDivider) => {
    if (!item) return;

    if (item instanceof DateDivider) {
      return <DateDividerListItem {...item} />;
    }

    const rc = item.userReadConfirmations && item.userReadConfirmations[0];
    const hasRead = item.hasRCRead();
    const hasUnread = item.hasRCUnread();
    const iconPadding = 25;

    return (
      <Stack styles={{ root: { paddingBottom: 5 } }}>
        <Stack horizontal verticalAlign="start" tokens={globalStackTokensGapSmall}>
          {hasUnread && <IPFontIcon {...circleIcon} style={{ color: 'red' }} />}
          {hasRead && <IPFontIcon {...validIcon} style={{ color: theme.palette.themePrimary }} />}
          {!hasUnread && !hasRead && <IPFontIcon {...circleIcon} style={{ color: theme.palette.themeLighter }} />}
          <Stack.Item>
            <IPText>
              <IPLink
                onClick={() => {
                  if (!item) return;
                  if (hasUnread) {
                    setShowConfirmationModal(item);
                  } else {
                    setLinkToShow(item);
                  }
                }}
              >
                {item.linkName}
              </IPLink>
            </IPText>
          </Stack.Item>
        </Stack>
        {hasUnread && rc && (
          <Stack.Item styles={{ root: { paddingLeft: iconPadding } }}>
            <IPText variant="small">{toLocaleDateShort(rc.created)}</IPText>
          </Stack.Item>
        )}
        {hasRead && rc && (
          <Stack.Item styles={{ root: { paddingLeft: iconPadding } }}>
            <IPText variant="small">{`${toLocaleDateShort(rc.readConfirmed)} ${t(
              'widgets:LibraryReadResponse.Version',
              { version: rc.version },
            )}`}</IPText>
          </Stack.Item>
        )}
      </Stack>
    );
  };

  //
  // Main render
  //
  if (isLoading) {
    return (
      <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
        <IPSpinner size={IPSpinnerSize.large} />
      </IPStack>
    );
  }

  if (links.length === 0) {
    return (
      <IPStack verticalFill>
        <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
          <DefaultEmptyStateImage size={DefaultEmptyStateSize.Small} />
        </IPStack>
        <IPStack.Item>
          <IPText>{t('widgets:LibraryReadResponse.NoItems')}</IPText>
        </IPStack.Item>
      </IPStack>
    );
  }

  return (
    <IPStack verticalFill styles={{ root: { paddingTop: 10 } }}>
      <IPStack.Item grow styles={{ root: { position: 'relative' } }}>
        <IPScrollablePane scrollbarVisibility={IPScrollbarVisibility.auto}>
          <IPList onRenderCell={onRenderItem} items={links} />
        </IPScrollablePane>
      </IPStack.Item>
      <LinkPreviewModalOrUrl
        isOpen={linkToShow !== undefined}
        onClose={() => setLinkToShow(undefined)}
        link={linkToShow}
        confirmOpen={false}
        enableLinks={true}
      />
      {showConfirmationModal !== undefined && (
        <LinkConfirmationModal
          links={getLinksToConfirm()}
          selectedLinkId={showConfirmationModal.linkId}
          isOpen={showConfirmationModal !== undefined}
          onClose={() => {
            setShowConfirmationModal(undefined);
            loadData();
          }}
        />
      )}
    </IPStack>
  );
};

export default WidgetLibraryMyItems;
